import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaFilter, FaWhatsapp, FaQuestionCircle } from "react-icons/fa";
import styles from "./after12.module.css";

import SocialSidebar from "../components/SocialSideBar";
import ENGINEERING from '../dataAfter12/ENGINEERING';
import IT_COMPS from '../dataAfter12/IT_COMPS';
import MEDICAL from '../dataAfter12/MEDICAL';
import INTERNATIONAL from '../dataAfter12/INTERNATIONAL';
import DEFENCE from '../dataAfter12/DEFENCE';
import PURE_SCIENCES from '../dataAfter12/PURE_SCIENCES';
import AGRI_BIO from '../dataAfter12/AGRI_BIO';
import ARCHITECTURE from '../dataAfter12/ARCHITECTURE';
import EARTH_ENV from '../dataAfter12/EARTH_ENV';
import COMMERCE_MGMT from '../dataAfter12/COMMERCE_MGMT';
import LAW_DESIGN_MEDIA from '../dataAfter12/LAW_DESIGN_MEDIA';
import SPORTS_EDU from '../dataAfter12/SPORTS_EDU';

/**
 * PrrepX Pathshala – Careers after 12th
 * - Mobile-first, responsive, accessible
 * - Search by course/college/exam/keywords
 * - Filter by Stream + Category + Exam chips
 * - Expandable cards with: What it is, Eligibility, Typical Entrance Exams, Top Colleges
 * - Data seeded from CBSE Compendium (standardized/condensed)
 */
const AlertTriangleIcon = () => (
     <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.disclaimerIcon}><path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></svg>
);
const STREAMS = [
  { id: "all", label: "Open to All" },
  { id: "science", label: "Science (PCM/PCB/PCMB)" },
  { id: "commerce", label: "Commerce" },
  { id: "arts", label: "Humanities/Arts" },
];

const EXAM_TAGS = [
  // ===== ENGINEERING & TECHNOLOGY =====
  "JEE Main", "JEE Main Paper 2", "JEE Advanced", "MHT-CET", "KCET", "TNEA", "UPSEE", "WBJEE", "OJEE", "MET", "ISI Entrance", "CMI Entrance",
  // ===== COMPUTER & INFORMATION SCIENCES =====
  "UCEED",
  // ===== MEDICINE & AYUSH =====
  "NEET UG", "AIIMS Nursing", "State AYUSH entrance exams", "State Nursing Entrance Exams", "State Physiotherapy Entrance Exams", "State Paramedical CETs", "State Pharmacy CETs",
  // ===== INTERNATIONAL PROGRAMS (SAT) =====
  "SAT",
  // ===== LAW, DESIGN, MEDIA & HUMANITIES =====
  "CLAT", "LSAT India", "CUET UG", "NCHMCT JEE", "IPU CET", "NATA", "NIFT Entrance", "NID Entrance",
  // ===== PURE & APPLIED SCIENCES =====
  "JNU Entrance", "Jamia Millia Islamia Entrance", "JEST for PG", "IISc Entrance",
  // ===== AGRICULTURE, LIFE & BIO SCIENCES =====
  "ICAR AIEEA", "State Agriculture CETs", "State Forestry CETs", "State Fisheries CETs",
  // ===== BUSINESS, COMMERCE & MANAGEMENT =====
  "IPMAT", "NPAT", "SET",
  // ===== EDUCATION, SPORTS & OTHERS =====
  "State University Entrance Exams",
  // ===== DEFENCE & ARMED FORCES =====
  "NDA & NA (UPSC)", "TES (Army) - Direct Entry", "Indian Navy B.Tech Entry", "AFCAT", "IAF X & Y Group Exam", "IMU-CET", "Coast Guard GD/Yantrik Exam", "CAPF Constable/Rifleman", "State Police Constable/SI Exam",
];

const CATEGORIES = [
  { id: "engineering", label: "Engineering & Technology" },
  { id: "it-comps", label: "Computer & Information Sciences" },
  { id: "medical", label: "Medicine & AYUSH" },
  { id: "defence", label: "Defence & Armed Forces" },
  { id: "international", label: "International Programs (SAT)" },
  { id: "architecture", label: "Architecture/Design" },
  { id: "commerce-mgmt", label: "Business, Commerce & Management" },
  { id: "law-design-media", label: "Law, Design, Media & Humanities" },
  { id: "agri-bio", label: "Agriculture, Life & Bio Sciences" },
  { id: "sports-edu", label: "Education, Sports & Others" },
  { id: "pure-sciences", label: "Pure & Applied Sciences" },
  { id: "earth-env", label: "Earth, Environment & Ocean" },
];


// ==================== COMPONENTS ====================
function CourseCard({ course, expanded, onToggle }) {
  return (
    <article className={styles.card} aria-expanded={expanded}>
      <header className={styles.card__header}>
        <div className={styles.card__title}>
          <h3>{course.name}</h3>
          <span className={styles.badge}>{course.categoryLabel}</span>
        </div>
        <button className={styles.link} onClick={onToggle}>
          {expanded ? "Hide details" : "View details"}
        </button>
      </header>

      <p className={styles.muted}>{course.summary}</p>
      <div className={styles.tags}>
        {course.exams.map((e) => (
          <span key={e} className={styles.tag}>{e}</span>
        ))}
      </div>

      {expanded && (
        <div className={styles.grid}>
          <div>
            <h4>Eligibility</h4>
            <p>{course.eligibility}</p>
          </div>
          <div>
            <h4>Typical Entrance Exams</h4>
            <ul className={styles.list}>
              {course.exams.map((e) => <li key={e}>{e}</li>)}
            </ul>
          </div>
          <div className={styles["grid-span-2"]}>
            <h4>Top Colleges (illustrative)</h4>
            <ul className={`${styles.list} ${styles["list--cols"]}`}>
              {course.colleges.map((c) => <li key={c}>{c}</li>)}
            </ul>
            <div className={styles.source}><span>Source: {course.source}</span></div>
          </div>
        </div>
      )}
    </article>
  );
}

export default function After12() {
  const [query, setQuery] = useState("");
  const [stream, setStream] = useState("all");
  const [category, setCategory] = useState("");
  const [examFilter, setExamFilter] = useState("");
  const [open, setOpen] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const courses = useMemo(() => {
    const catMap = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));
    const ALL_COURSES = [
      ...ENGINEERING,
      ...IT_COMPS,
      ...MEDICAL,
      ...INTERNATIONAL,
      ...DEFENCE,
      ...PURE_SCIENCES,
      ...AGRI_BIO,
      ...ARCHITECTURE,
      ...EARTH_ENV,
      ...COMMERCE_MGMT,
      ...LAW_DESIGN_MEDIA,
      ...SPORTS_EDU,
    ];
    return ALL_COURSES.map((c) => ({ ...c, categoryLabel: catMap[c.category] }));
  }, []);

  const filtered = useMemo(() => {
    return courses.filter((c) => {
      const q = query.trim().toLowerCase();
      const matchesSearch =
        !q || [c.name, c.summary, c.eligibility, ...c.colleges, ...(c.keywords || [])]
          .join(" ").toLowerCase().includes(q);

      const matchesStream = stream === "all" ? true : c.stream === stream || c.stream === "all";
      const matchesCategory = !category || c.category === category;
      const matchesExam = !examFilter || c.exams.includes(examFilter);

      return matchesSearch && matchesStream && matchesCategory && matchesExam;
    });
  }, [courses, query, stream, category, examFilter]);

  function clearAll() {
    setQuery("");
    setStream("all");
    setCategory("");
    setExamFilter("");
    setOpen({});
    setCurrentPage(1);
  }

  return (
    <div className={styles.after12PageOuterWrapper}>
      
      <SocialSidebar />
      
        <section className={styles.hero}>
          <div className={styles.heroContent}> 
            <div className={styles.hero__text}>
              <h1>Career Guidance After Class 12th</h1>
              <h2>What Next After 12th? Explore Courses, Exams & Colleges after Class 12th with PrrepX Pathshala</h2>
              <div className={styles.ctaContainer}>
                  <strong className={styles.ctaText}>Need Further Guidance or Mentorship? You can connect directly via</strong>
                  <div className={styles.ctaButtonsWrapper}>
                    <Link to="/enquiry-now" className={`${styles.ctaButton} ${styles.doubtButton}`}>
                      <FaQuestionCircle /> Contact Us
                    </Link>
                    <a 
                      href="https://wa.me/918287648766" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`${styles.ctaButton} ${styles.whatsappButton}`}
                    >
                      <FaWhatsapp /> WhatsApp
                    </a>
                    
                  </div>
                </div>
            </div>
            <div className={styles.disclaimerBox} role="alert">
                  <AlertTriangleIcon />
                  <p>
                    <strong>Disclaimer: </strong>
                    Meticulously compiled by PrrepX Pathshala Team; however, for the most current and definitive details, please refer to official university and college websites.
                  </p>
              </div>
          </div>
        </section>
    
    <main className={styles.after12thBody}>
        <section className={styles.controls}>
          <h2 className={styles.controls__title}>🔍 Apply Filters(Use one fiter at a time to search effectively).</h2>
          <div className={styles.filtersRow}>
            <div className={styles.inputWrapper}>
              <FaSearch className={styles.icon} />
              <input
                type="text"
                placeholder="Search by course, exam, or college..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className={styles.filterInput}
              />
            </div>

            <div className={styles.selectWrapper}>
              <FaFilter className={styles.icon} />
              <select
                value={stream}
                onChange={(e) => {
                  setStream(e.target.value);
                  setCurrentPage(1);
                }}
                className={styles.filterSelect}
              >
                {STREAMS.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>

            <div className={styles.selectWrapper}>
              <FaFilter className={styles.icon} />
              <select
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className={styles.filterSelect}
              >
                <option value="">All Categories</option>
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className={styles.selectWrapper}>
              <FaFilter className={styles.icon} />
              <select
                value={examFilter}
                onChange={(e) => {
                  setExamFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={styles.filterSelect}
              >
                <option value="">All Exams</option>
                {EXAM_TAGS.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            </div>

            <button className={`${styles.btn} ${styles["btn--secondary"]}`} onClick={clearAll}>Clear Filters</button>
          </div>

          <div className={styles.controls__footer}>
            <div className={styles.resultCount} aria-live="polite">
              Showing {filtered.length} course{filtered.length !== 1 ? "s" : ""}
            </div>
          </div>
        </section>

        <section className={styles.results}>
          {filtered.length === 0 ? (
            <div className={styles.empty}>
              <p>No matches. Try removing some filters or different keywords.</p>
            </div>
          ) : (
            filtered.map((c) => (
              <CourseCard
                key={c.id}
                course={c}
                expanded={!!open[c.id]}
                onToggle={() => setOpen((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}
              />
            ))
          )}
        </section>

        <footer className={styles.footer}>
          <p>Meticulously compiled by PrrepX Pathshala; however, for the most current and definitive details, please refer to official university and college websites. © {new Date().getFullYear()} PrrepX Pathshala</p>
        </footer>
      </main>
    </div>
  );
}